import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { CustomerService } from '../../../../service/customerService';
import { toast } from 'react-toastify';
import useAbortController from '../../../../hooks/useAbortController';
import { isLocalDev } from '../../../../utils/environment';

export default function ManagePolicyFamilyLinksDialog({ open, onClose, customer, policy }) {
    const [signal, abortRequest] = useAbortController();
    const [state, setState] = useState({ loading: false, familyMembers: [] });

    const loadLinks = useCallback(() => {
        if (!open || !customer?.customer?.id || !policy?.id) return;

        setState((current) => ({ ...current, loading: true }));
        CustomerService.getInsurancePolicyFamilyLinks(customer.customer.id, policy.id, signal)
            .then((result) => {
                const { code, data } = result;
                if (code === 0) {
                    setState({
                        loading: false,
                        familyMembers: data.familyMembers || [],
                    });
                } else {
                    setState((current) => ({ ...current, loading: false }));
                    toast.error('Unable to load family links');
                }
            })
            .catch((error) => {
                if (isLocalDev) {
                    console.error(error);
                }
                setState((current) => ({ ...current, loading: false }));
            });
    }, [open, customer?.customer?.id, policy?.id, signal]);

    useEffect(() => {
        loadLinks();
    }, [loadLinks]);

    useEffect(() => {
        return () => abortRequest();
    }, [abortRequest]);

    const refresh = () => loadLinks();

    const performLinkMember = async (memberId, silent = false) => {
        try {
            const response = await CustomerService.linkInsurancePolicyToCustomer(customer.customer.id, policy.id, memberId);
            const { code, message } = response;
            if (code === 0) {
                if (!silent) {
                    toast.success(message);
                    refresh();
                }
                return true;
            } else {
                if (!silent) {
                    toast.error(message || 'Failed to link member');
                }
                return false;
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            if (!silent) {
                toast.error('Failed to link member');
            }
            return false;
        }
    };

    const performUnlinkMember = async (memberId, silent = false) => {
        try {
            const response = await CustomerService.unlinkInsurancePolicy(memberId, policy.id);
            const { code, message } = response;
            if (code === 0) {
                if (!silent) {
                    toast.success(message);
                    refresh();
                }
                return true;
            } else {
                if (!silent) {
                    toast.error(message || 'Failed to unlink member');
                }
                return false;
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            if (!silent) {
                toast.error('Failed to unlink member');
            }
            return false;
        }
    };

    const handleLinkAll = async () => {
        const missingMembers = state.familyMembers.filter((member) => !member.linked);
        let linkedCount = 0;

        for (const member of missingMembers) {
            // Sequential requests keep the UI predictable and preserve partial progress.
            const didLink = await performLinkMember(member.id, true);
            if (didLink) {
                linkedCount += 1;
            }
        }

        if (linkedCount > 0) {
            toast.success(`Linked ${linkedCount} family member${linkedCount === 1 ? '' : 's'}`);
            refresh();
        } else if (missingMembers.length === 0) {
            toast.info('All family members are already linked');
        }
    };

    const handleUnlinkAll = async () => {
        try {
            const response = await CustomerService.unlinkInsurancePolicyFromFamily(customer.customer.id, policy.id);
            const { code, message } = response;
            if (code === 0) {
                toast.success(message);
                refresh();
            } else {
                toast.error(message || 'Failed to unlink family');
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            toast.error('Failed to unlink family');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Manage Family Links
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                    <Chip label={policy?.providerName || 'Policy'} color="primary" />
                    <Chip label={policy?.policyNumber || 'Policy #'} variant="outlined" />
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Alert severity="info">
                        Link or unlink this policy for each family member. Use bulk actions when the whole family should share the same coverage state.
                    </Alert>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button variant="contained" onClick={handleLinkAll} disabled={!state.familyMembers.some((member) => !member.linked)}>
                            Link Missing Members
                        </Button>
                        <Button variant="outlined" color="error" onClick={handleUnlinkAll} disabled={!customer?.customer?.familyGroupId}>
                            Unlink All Family Members
                        </Button>
                    </Stack>

                    <Divider />

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Family Member</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.loading ? (
                                <TableRow>
                                    <TableCell colSpan={3}>Loading...</TableCell>
                                </TableRow>
                            ) : state.familyMembers.filter((member) => member.id !== customer?.customer?.id).length > 0 ? (
                                state.familyMembers
                                    .filter((member) => member.id !== customer?.customer?.id)
                                    .map((member) => (
                                        <TableRow key={member.id}>
                                            <TableCell>{member.fullName || `${member.firstName} ${member.lastName}`}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={member.linked ? 'Linked' : 'Not linked'}
                                                    color={member.linked ? 'success' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {member.linked ? (
                                                    <Button color="error" variant="text" onClick={() => performUnlinkMember(member.id)}>
                                                        Unlink
                                                    </Button>
                                                ) : (
                                                    <Button variant="text" onClick={() => performLinkMember(member.id)}>
                                                        Link
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <Typography color="text.secondary">
                                            No family members available for this customer.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}